import React, { useState } from 'react';
import { Button, Checkbox, Collapse, ConfigProvider, Dropdown, Flex, Input, Modal, Select, Space, Tooltip } from 'antd';
import {
	AppstoreOutlined,
	CopyOutlined,
	DeleteOutlined,
	EditOutlined,
	EllipsisOutlined,
	EyeOutlined,
	FolderOutlined,
} from '@ant-design/icons';
import Text from 'admin/components/common/Text';
import ResourceAddButton from 'admin/components/common/ResourceEditor/ResourceAddButton';
import ResourceActionButton from 'admin/components/common/ResourceEditor/ResourceActionButton';
import ResourceEmptyState from 'admin/components/common/ResourceEditor/ResourceEmptyState';
import { Search } from 'admin/components/common/Search';
import { antdThemeDark, antdThemeLight, ThemeProvider } from 'admin/components/common/Theme';
import {
	DESIGN_PRESET_PROPERTIES,
	createDesignTokenId,
	getDefaultDesignModeSelection,
	normalizeDesignDefinitionPresetPlatforms,
	type DesignDefinition,
	type DesignPreset,
	type DesignPresetProperty,
	type DesignPresetValue,
} from 'common/design-tokens';
import {
	getDesignPresetNameParts,
	getDesignPresetFolderPaths,
	groupDesignPresets,
	moveDesignPresets,
	renameDesignPresetFolder,
	type PresetFolderNode,
} from 'common/design-tokens/preset-paths';
import { getPlatformReferenceWidth } from 'common/utils/story-media-query';
import { DEFAULT_MEDIA_QUERY_PLATFORMS } from 'common/constants/story-media-query';
import { generateId } from 'utils/generate-id';
import t from 'utils/translate';
import type { StoryDefaultPlatform, StoryMediaQuery } from 'types/story';
import DesignPresetPreview, { type DesignPresetPreviewFont } from './DesignPresetPreview';
import DesignPresetDetailsModal from './DesignPresetDetailsModal';
import DesignPresetSummary from './DesignPresetSummary';
import {
	getDesignPresetPlatformName,
	DESIGN_PRESET_PREVIEW_MEDIA_QUERY,
	orderDesignPresetPlatforms,
} from './design-preset-display';
import DesignPresetPlatformLabel from './DesignPresetPlatformLabel';
import { PlatformAssignment, defaultLiteral } from './DesignPresetAssignment';
import {
	PRESET_PROPERTY_GROUPS,
	updatePresetAssignments,
	getPresetPropertyRowKeys,
	type PresetAssignmentChange,
	type PresetPropertyGroup,
} from './design-preset-editing';
import DesignPresetGroupCell from './DesignPresetGroupCell';
import DesignPresetName from './DesignPresetName';
import css from './DesignPresetsEditor.scss';

const compactCollapseStyles: React.ComponentProps<typeof Collapse>['styles'] = {
	header: { padding: '6px 8px', alignItems: 'center' },
	title: { minWidth: 0 },
	body: { padding: 0 },
};

const countPresets = (nodes: PresetFolderNode[]): number =>
	nodes.reduce((count, node) => count + (node.kind === 'preset' ? 1 : countPresets(node.children)), 0);

type PropertyRow = {
	key: string;
	label: string;
	properties: readonly DesignPresetProperty[];
	group?: PresetPropertyGroup;
};
const groupedProperties: readonly PresetPropertyGroup[] = PRESET_PROPERTY_GROUPS;
const propertyRows = DESIGN_PRESET_PROPERTIES.flatMap<PropertyRow>(property => {
	const group = groupedProperties.find(item => item.properties.includes(property));
	if (group)
		return property === group.properties[0]
			? [{ key: group.id, label: group.label, properties: group.properties, group }]
			: [];
	return [{ key: property, label: t(`presets.properties.${property}`), properties: [property] }];
});

export default function DesignPresetsEditor(props: React.ComponentProps<typeof PresetEditor>) {
	const theme = props.theme ?? 'light';
	return (
		<ConfigProvider theme={theme === 'dark' ? antdThemeDark : antdThemeLight}>
			<ThemeProvider context={{ theme }}>
				<PresetEditor {...props} />
			</ThemeProvider>
		</ConfigProvider>
	);
}

function PresetEditor({
	value: inputValue,
	onChange,
	fonts,
	mediaQuery = DESIGN_PRESET_PREVIEW_MEDIA_QUERY,
	theme = 'light',
	disabled,
	getPresetUsageCount,
}: {
	value: DesignDefinition;
	onChange: (value: DesignDefinition) => void;
	fonts: DesignPresetPreviewFont[];
	mediaQuery?: StoryMediaQuery;
	theme?: 'light' | 'dark';
	disabled?: boolean;
	getPresetUsageCount?: (id: string) => number;
}) {
	const value = normalizeDesignDefinitionPresetPlatforms(inputValue, mediaQuery.defaultPlatform);
	const presets = value.presets ?? [];
	const [previewId, setPreviewId] = useState<string>();
	const previewPreset = presets.find(preset => preset.id === previewId);
	const [presetDialog, setPresetDialog] = useState<
		{ action: 'create' } | { action: 'description'; preset: Pick<DesignPreset, 'id' | 'name' | 'description'> }
	>();
	const [activeKeys, setActiveKeys] = useState<string[]>([]);
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [addedRows, setAddedRows] = useState<Record<string, string[]>>({});
	const hasRow = (preset: DesignPreset, row: PropertyRow) =>
		row.properties.some(property => preset.properties[property]) || (addedRows[preset.id] ?? []).includes(row.key);
	const [search, setSearch] = useState('');
	const [folderDialog, setFolderDialog] = useState<{ action: 'add' | 'move'; name: string }>();
	const [modal, contextHolder] = Modal.useModal();
	const selected = selectedIds.filter(id => presets.some(preset => preset.id === id));
	const query = search.trim().toLowerCase();
	const filteredPresets = presets.filter(preset =>
		`${preset.name} ${preset.description ?? ''}`.toLowerCase().includes(query)
	);
	const groups = groupDesignPresets(filteredPresets);
	const visibleKeys = query
		? [...activeKeys, ...getDesignPresetFolderPaths(filteredPresets).map(path => `folder:${path.join('/')}`)]
		: activeKeys;
	const folderPaths = getDesignPresetFolderPaths(presets);
	const deletePreset = (preset: DesignPreset) => {
		const remove = () => onChange({ ...value, presets: presets.filter(item => item.id !== preset.id) });
		const count = getPresetUsageCount?.(preset.id) ?? 0;
		if (!count) {
			remove();
			return;
		}
		modal.confirm({
			title: `Delete ${preset.name}?`,
			content: (
				<Text compact size={Text.size.body}>
					{count} linked {count === 1 ? 'block' : 'blocks'} will lose this preset’s shared styling. Local
					styles stay; bindings become unresolved. Detach the preset from blocks first to preserve its
					assignments.
				</Text>
			),
			okText: 'Delete preset',
			okButtonProps: { danger: true },
			cancelText: 'Cancel',
			onOk: remove,
		});
	};
	const modeSelection = getDefaultDesignModeSelection(value.modeGroups);
	const platforms = orderDesignPresetPlatforms(mediaQuery, presets);
	const columns = platforms.flatMap(item =>
		item.key === DEFAULT_MEDIA_QUERY_PLATFORMS.DEFAULT
			? []
			: [
					{
						key: item.key,
						assignmentPlatform: item.key === mediaQuery.defaultPlatform ? ('base' as const) : item.key,
						label: mediaQuery.config[item.key]
							? getDesignPresetPlatformName(item.key, item.config.uiName)
							: `${getDesignPresetPlatformName(item.key)} (${t('presets.unsupportedPlatform')})`,
					},
				]
	);
	const update = (id: string, change: (preset: DesignPreset) => DesignPreset) =>
		onChange({ ...value, presets: presets.map(preset => (preset.id === id ? change(preset) : preset)) });
	const setAssignments = (
		id: string,
		platform: 'base' | StoryDefaultPlatform,
		changes: readonly PresetAssignmentChange[]
	) => update(id, preset => updatePresetAssignments(preset, platform, changes));
	const setAssignment = (
		id: string,
		property: DesignPresetProperty,
		platform: 'base' | StoryDefaultPlatform,
		next: DesignPresetValue | undefined
	) => setAssignments(id, platform, [{ property, value: next }]);
	const add = (preset: DesignPreset) => {
		onChange({ ...value, presets: [preset, ...presets] });
		const path = getDesignPresetNameParts(preset.name).slice(0, -1);
		setSearch('');
		setActiveKeys(current => [
			...new Set([
				...current,
				preset.id,
				...path.map((_, index) => `folder:${path.slice(0, index + 1).join('/')}`),
			]),
		]);
	};
	const duplicate = (source: DesignPreset) =>
		add({
			...structuredClone(source),
			id: createDesignTokenId(
				`${source.id}-copy`,
				presets.map(item => item.id)
			),
			name: `${source.name} ${t('presets.copy')}`,
		});

	const getPresetItem = (preset: DesignPreset) => {
		const rows = getPresetPropertyRowKeys(preset, addedRows[preset.id] ?? []).flatMap(key => {
			const row = propertyRows.find(item => item.key === key);
			return row ? [row] : [];
		});
		const propertyCount = Object.keys(preset.properties).length;
		const addPropertyAction = (
			<Dropdown
				trigger={['click']}
				disabled={disabled || propertyRows.every(row => hasRow(preset, row))}
				menu={{
					items: propertyRows
						.filter(row => !hasRow(preset, row))
						.map(row => ({ key: row.key, label: row.label })),
					onClick: ({ key }) => {
						const row = propertyRows.find(item => item.key === key);
						if (!row) return;
						setAddedRows(current => ({
							...current,
							[preset.id]: [...(current[preset.id] ?? []), key],
						}));
						if (!row.group)
							setAssignment(
								preset.id,
								row.properties[0],
								'base',
								defaultLiteral(row.properties[0], value)
							);
					},
				}}
			>
				<ResourceAddButton
					prominent={!rows.length}
					theme={theme}
					disabled={disabled || propertyRows.every(row => hasRow(preset, row))}
				>
					{t('presets.addProperty')}
				</ResourceAddButton>
			</Dropdown>
		);
		return {
			key: preset.id,
			className: `${css.presetItem}${selected.includes(preset.id) ? ` ${css.selectedPreset}` : ''}`,
			styles: { header: { padding: '14px 18px' }, body: { padding: '16px 18px' } },
			label: (
				<span className={css.presetHeading}>
					<Checkbox
						disabled={disabled}
						checked={selected.includes(preset.id)}
						onClick={event => event.stopPropagation()}
						onChange={event =>
							setSelectedIds(current =>
								event.target.checked ? [...current, preset.id] : current.filter(id => id !== preset.id)
							)
						}
						aria-label={`Select ${preset.name}`}
					/>
					<DesignPresetSummary
						preset={preset}
						design={value}
						mediaQuery={mediaQuery}
						modeSelection={modeSelection}
						layout="control"
						detailsEnabled={false}
						name={
							<DesignPresetName
								kind="preset"
								name={getDesignPresetNameParts(preset.name).at(-1) ?? preset.name}
								path={getDesignPresetNameParts(preset.name)}
								disabled={disabled}
								onRename={name =>
									update(preset.id, current => ({
										...current,
										name: [...getDesignPresetNameParts(current.name).slice(0, -1), name].join('/'),
									}))
								}
							/>
						}
					/>
					{propertyCount > 0 && (
						<span className={css.count}>
							{propertyCount} {propertyCount === 1 ? 'property' : 'properties'}
						</span>
					)}
					<Space size={0} className={css.rowActions}>
						{propertyCount > 0 && (
							<Tooltip title={t('presets.preview')}>
								<Button
									type="text"
									size="small"
									icon={<EyeOutlined />}
									aria-label={t('presets.preview')}
									onClick={event => {
										event.stopPropagation();
										setPreviewId(preset.id);
									}}
									onKeyDown={event => event.stopPropagation()}
								/>
							</Tooltip>
						)}
						<Dropdown
							trigger={['click']}
							disabled={disabled}
							menu={{
								items: [
									{ key: 'description', label: 'Edit description', icon: <EditOutlined /> },
									{ key: 'duplicate', label: t('presets.duplicate'), icon: <CopyOutlined /> },
									{ key: 'move', label: 'Move to folder', icon: <FolderOutlined /> },
									{
										key: 'delete',
										label: t('designTokens.delete'),
										icon: <DeleteOutlined />,
										danger: true,
									},
								],
								onClick: ({ key, domEvent }) => {
									domEvent.stopPropagation();
									if (key === 'description')
										setPresetDialog({
											action: 'description',
											preset: {
												id: preset.id,
												name: preset.name,
												description: preset.description,
											},
										});
									if (key === 'duplicate') duplicate(preset);
									if (key === 'move') {
										setSelectedIds([preset.id]);
										setFolderDialog({
											action: 'move',
											name: getDesignPresetNameParts(preset.name).slice(0, -1).join('/'),
										});
									}
									if (key === 'delete') deletePreset(preset);
								},
							}}
						>
							<Button
								type="text"
								size="small"
								disabled={disabled}
								icon={<EllipsisOutlined />}
								aria-label={t('presets.actions')}
								onClick={event => event.stopPropagation()}
								onKeyDown={event => event.stopPropagation()}
							/>
						</Dropdown>
					</Space>
				</span>
			),
			children: (
				<Flex vertical>
					{preset.description ? (
						<Text compact size={Text.size.body} className={css.muted}>
							{preset.description}
						</Text>
					) : null}
					{rows.length > 0 && (
						<div
							className={css.tableScroll}
							role="region"
							aria-label={`${preset.name} ${t('presets.propertiesLabel')}`}
							// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- keyboard scrolling
							tabIndex={0}
						>
							<table className={css.table} style={{ minWidth: 160 + columns.length * 145 }}>
								<thead>
									<tr>
										<th scope="col">{t('presets.propertiesLabel')}</th>
										{columns.map(column => (
											<th
												key={column.key}
												scope="col"
												data-default={column.assignmentPlatform === 'base'}
											>
												<DesignPresetPlatformLabel
													platform={column.key}
													defaultPlatform={mediaQuery.defaultPlatform}
													label={column.label}
													referenceWidth={getPlatformReferenceWidth(mediaQuery, column.key)}
												/>
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{rows.map(row => (
										<tr key={row.key}>
											<th scope="row">
												<div className={css.propertyName}>
													{row.label}
													<Button
														type="text"
														disabled={disabled}
														size="small"
														className={css.removeProperty}
														aria-label={`${t('designTokens.delete')} ${row.label}`}
														icon={<DeleteOutlined />}
														onClick={() => {
															update(preset.id, current => {
																const properties = { ...current.properties };
																row.properties.forEach(
																	property => delete properties[property]
																);
																return { ...current, properties };
															});
															setAddedRows(current => ({
																...current,
																[preset.id]: (current[preset.id] ?? []).filter(
																	key => key !== row.key
																),
															}));
														}}
													/>
												</div>
											</th>
											{columns.map(column => (
												<td
													key={column.key}
													data-default={column.assignmentPlatform === 'base'}
												>
													{row.group ? (
														<DesignPresetGroupCell
															group={row.group}
															preset={preset}
															platform={column.assignmentPlatform}
															platformLabel={column.label}
															design={value}
															mediaQuery={mediaQuery}
															fonts={fonts}
															modeSelection={modeSelection}
															disabled={disabled}
															onChange={changes =>
																setAssignments(
																	preset.id,
																	column.assignmentPlatform,
																	changes
																)
															}
														/>
													) : (
														<PlatformAssignment
															property={row.properties[0]}
															preset={preset}
															platform={column.assignmentPlatform}
															design={value}
															mediaQuery={mediaQuery}
															fonts={fonts}
															modeSelection={modeSelection}
															disabled={disabled}
															onChange={next =>
																setAssignment(
																	preset.id,
																	row.properties[0],
																	column.assignmentPlatform,
																	next
																)
															}
														/>
													)}
												</td>
											))}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
					{rows.length ? (
						<div className={css.presetFooter}>{addPropertyAction}</div>
					) : (
						<ResourceEmptyState
							inset
							title="Add your first property"
							description="Add typography, colors or spacing to define this preset."
							action={addPropertyAction}
						/>
					)}
				</Flex>
			),
		};
	};
	const renameFolder = (path: string[], name: string) => {
		onChange({ ...value, presets: renameDesignPresetFolder(presets, path, name) });
		const previous = `folder:${path.join('/')}`;
		const next = `folder:${[...path.slice(0, -1), name].join('/')}`;
		setActiveKeys(current => [
			...new Set(
				current.map(key =>
					key === previous || key.startsWith(`${previous}/`) ? next + key.slice(previous.length) : key
				)
			),
		]);
	};
	const getFolderItems = (nodes: PresetFolderNode[]): NonNullable<React.ComponentProps<typeof Collapse>['items']> =>
		nodes.map(node => {
			if (node.kind === 'preset') return getPresetItem(node.preset);
			const key = `folder:${node.path.join('/')}`;
			return {
				key,
				className: css.folderItem,
				styles: { body: { padding: '0 0 0 16px' } },
				label: (
					<Flex align="center" gap={8} className={css.folderHeading}>
						<FolderOutlined />
						<DesignPresetName
							kind="folder"
							name={node.name}
							path={node.path}
							disabled={disabled}
							onRename={name => renameFolder(node.path, name)}
						/>
						<span className={css.count}>{countPresets(node.children)}</span>
					</Flex>
				),
				children: (
					<Collapse
						ghost
						size="small"
						styles={compactCollapseStyles}
						activeKey={visibleKeys}
						onChange={keys => changeActiveKeys(node.children, keys)}
						items={getFolderItems(node.children)}
					/>
				),
			};
		});
	const changeActiveKeys = (nodes: PresetFolderNode[], keys: string | string[]) => {
		const siblings = new Set(
			nodes.map(node => (node.kind === 'folder' ? `folder:${node.path.join('/')}` : node.preset.id))
		);
		const next = typeof keys === 'string' ? [keys] : keys;
		setActiveKeys(current => [...current.filter(key => !siblings.has(key)), ...next]);
	};
	const saveFolder = () => {
		if (!folderDialog) return;
		if (folderDialog.action !== 'move' && !getDesignPresetNameParts(folderDialog.name).length) return;
		const next = moveDesignPresets(presets, selected, getDesignPresetNameParts(folderDialog.name));
		onChange({ ...value, presets: next });
		const path = getDesignPresetNameParts(folderDialog.name);
		setActiveKeys(current => [
			...new Set([...current, ...path.map((_, index) => `folder:${path.slice(0, index + 1).join('/')}`)]),
		]);
		setFolderDialog(undefined);
	};

	return (
		<Flex vertical gap={12} className={css.root}>
			{contextHolder}
			{presets.length > 0 ? (
				<>
					<Flex gap="small" align="center" wrap className={css.toolbar}>
						<Search
							stylePreset="inline"
							className={css.search}
							aria-label="Search design presets"
							placeholder="Search presets"
							value={search}
							onSearchChange={event => setSearch(event.target.value)}
							onClear={() => setSearch('')}
						/>
						<Flex gap={4} align="center" className={css.toolbarActions}>
							<Tooltip
								title={!selected.length ? 'Select presets to add them to a new folder' : undefined}
							>
								<span>
									<ResourceActionButton
										theme={theme}
										icon={<FolderOutlined />}
										disabled={disabled || !selected.length}
										onClick={() => setFolderDialog({ action: 'add', name: '' })}
									>
										Add folder
									</ResourceActionButton>
								</span>
							</Tooltip>
							<ResourceActionButton
								theme={theme}
								disabled={disabled || !selected.length}
								onClick={() => setFolderDialog({ action: 'move', name: '' })}
							>
								Move to folder
							</ResourceActionButton>
							<ResourceAddButton
								theme={theme}
								onClick={() => setPresetDialog({ action: 'create' })}
								disabled={disabled}
							>
								{t('presets.add')}
							</ResourceAddButton>
						</Flex>
					</Flex>
					<Flex gap="small" justify="space-between" align="center" wrap className={css.libraryMeta}>
						<Flex gap="small" align="center">
							<Text tag="span" compact size={Text.size.body} className={css.muted}>
								{selected.length
									? `${selected.length} selected`
									: `${presets.length} ${presets.length === 1 ? 'preset' : 'presets'}`}
							</Text>
							{selected.length > 0 && (
								<ResourceActionButton theme={theme} onClick={() => setSelectedIds([])}>
									Clear selection
								</ResourceActionButton>
							)}
						</Flex>
					</Flex>
					{groups.length > 0 && (
						<Collapse
							ghost
							size="small"
							className={css.library}
							styles={compactCollapseStyles}
							activeKey={visibleKeys}
							onChange={keys => changeActiveKeys(groups, keys)}
							items={getFolderItems(groups)}
						/>
					)}
					{query && !groups.length && presets.length > 0 && (
						<Text compact size={Text.size.body} className={css.empty}>
							{t('presets.noMatches')}
						</Text>
					)}
				</>
			) : (
				<ResourceEmptyState
					title="No design presets yet"
					description="Create a preset to reuse typography, colors and spacing across elements."
					icon={<AppstoreOutlined />}
					action={
						<ResourceAddButton
							prominent
							theme={theme}
							disabled={disabled}
							onClick={() => setPresetDialog({ action: 'create' })}
						>
							{t('presets.add')}
						</ResourceAddButton>
					}
				/>
			)}
			{previewPreset ? (
				<DesignPresetPreview
					preset={previewPreset}
					design={value}
					mediaQuery={mediaQuery}
					platforms={columns}
					fonts={fonts}
					onClose={() => setPreviewId(undefined)}
				/>
			) : null}
			{presetDialog && (
				<DesignPresetDetailsModal
					initialValue={presetDialog.action === 'description' ? presetDialog.preset : undefined}
					theme={theme}
					disabled={disabled}
					onClose={() => setPresetDialog(undefined)}
					onSave={details => {
						if (presetDialog.action === 'description') {
							update(presetDialog.preset.id, preset => ({ ...preset, description: details.description }));
						} else
							add({
								id: createDesignTokenId(
									`preset-${generateId(12)}`,
									presets.map(item => item.id),
									'preset'
								),
								...details,
								properties: {},
							});
						setPresetDialog(undefined);
					}}
				/>
			)}
			<Modal
				open={!!folderDialog}
				title={folderDialog?.action === 'move' ? 'Move to folder' : 'Add folder'}
				onCancel={() => setFolderDialog(undefined)}
				onOk={saveFolder}
				okText={t('designTokens.save')}
				okButtonProps={{
					disabled:
						disabled ||
						(folderDialog?.action !== 'move' && !getDesignPresetNameParts(folderDialog?.name ?? '').length),
				}}
			>
				<Flex vertical gap={12}>
					<Text compact size={Text.size.body} className={css.muted}>
						{`${selected.length} selected ${selected.length === 1 ? 'preset' : 'presets'}`}
					</Text>
					{folderDialog?.action === 'move' ? (
						<Select
							style={{ width: '100%' }}
							aria-label="Destination folder"
							showSearch
							value={folderDialog.name}
							options={[
								{ value: '', label: 'Root' },
								...folderPaths.map(path => ({ value: path.join('/'), label: path.join('/') })),
							]}
							onChange={name => setFolderDialog(current => current && { ...current, name })}
						/>
					) : (
						<Input
							autoFocus
							aria-label="Folder name"
							placeholder="For example Buttons/Primary"
							value={folderDialog?.name ?? ''}
							onChange={event =>
								setFolderDialog(current => current && { ...current, name: event.target.value })
							}
						/>
					)}
				</Flex>
			</Modal>
		</Flex>
	);
}
